import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Timer, Trophy, ArrowLeft, Star } from 'lucide-react';
import { cn } from '../lib/utils';
import HowToPlay from './HowToPlay';
import FeverOverlay from './FeverOverlay';
import GameModal from './GameModal';

import { audioEngine } from '../utils/audioEngine';
import { PrismBuffs } from '../utils/prismSystem';

interface AppleGameProps {
  onGameOver: (stats: { 
    score: number;
    correctAnswers: number; 
    mistakes: number; 
    feverAnswers: number;
    feverTriggerCount: number;

    bonusExp: number;
  }) => void;
  onBack: () => void;
  onUpdateMission?: (id: string, amount?: number) => void;
  prismBuffs: PrismBuffs;
  isPractice?: boolean;
  tutorialStep?: number;
  onNextTutorialStep?: () => void;
}

const ROWS = 11;
const COLS = 8;
const INITIAL_TIME = 60;

const TUTORIAL_BOXES = [
  { r1: 2, r2: 3, c1: 2, c2: 3, label: "SUM TO 10 (+1 Pt)" },
  { r1: 5, r2: 6, c1: 1, c2: 3, label: "SUM TO 20 (+2 Pts)" },
  { r1: 8, r2: 9, c1: 4, c2: 6, label: "SUM TO 30 (+3 Pts)" }
];

const isInsideTutorialBox = (r: number, c: number) => {
  return TUTORIAL_BOXES.some(box => r >= box.r1 && r <= box.r2 && c >= box.c1 && c <= box.c2);
};

const StarIcon = ({ value, isSelected }: { value: number, isSelected?: boolean }) => {
  return (
    <div className={cn(
      "w-full h-full relative flex items-center justify-center transition-all duration-300",
      isSelected && "scale-110"
    )}>
      {/* Hoshi Paper Star Shape */}
      <svg viewBox="0 0 100 100" className={cn(
        "absolute inset-0 w-full h-full drop-shadow-[0_4px_6px_rgba(0,0,0,0.3)]",
        isSelected ? "drop-shadow-[0_0_20px_rgba(255,255,255,0.9)]" : ""
      )}>
        <defs>
          <linearGradient id="starGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fef08a" />
            <stop offset="100%" stopColor="#f59e0b" />
          </linearGradient>
          <linearGradient id="starSelectedGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="100%" stopColor="#fde047" />
          </linearGradient>
          <filter id="starGlow" x="-20%" y="-20%" width="140%" height="140%">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feComposite in="SourceGraphic" in2="blur" operator="over" />
          </filter>
        </defs>

        {/* Base shadow layer */}
        <path 
          d="M 50 10 L 70 32 L 95 42 L 80 65 L 75 92 L 50 80 L 25 92 L 20 65 L 5 42 L 30 32 Z" 
          fill={isSelected ? "rgba(255,255,255,0.5)" : "rgba(217,119,6,0.4)"}
          transform="translate(0, 4)"
        />

        {/* Main Base Body */}
        <path 
          d="M 50 10 L 70 32 L 95 42 L 80 65 L 75 92 L 50 80 L 25 92 L 20 65 L 5 42 L 30 32 Z" 
          fill={isSelected ? "url(#starSelectedGrad)" : "url(#starGrad)"}
          className="transition-colors duration-300"
          stroke="rgba(255,255,255,0.3)"
          strokeWidth="1"
        />

        {/* 3D Paper Folds */}
        <path d="M 50 52 L 30 32 L 50 10 Z" fill="white" fillOpacity="0.4" />
        <path d="M 50 52 L 5 42 L 30 32 Z" fill="white" fillOpacity="0.25" />
        
        <path d="M 50 52 L 50 10 L 70 32 Z" fill="white" fillOpacity="0.15" />
        <path d="M 50 52 L 70 32 L 95 42 Z" fill="white" fillOpacity="0.2" />

        <path d="M 50 52 L 95 42 L 80 65 Z" fill="black" fillOpacity="0.05" />
        <path d="M 50 52 L 80 65 L 75 92 Z" fill="black" fillOpacity="0.15" />
        <path d="M 50 52 L 75 92 L 50 80 Z" fill="black" fillOpacity="0.25" />

        <path d="M 50 52 L 50 80 L 25 92 Z" fill="black" fillOpacity="0.2" />
        <path d="M 50 52 L 25 92 L 20 65 Z" fill="black" fillOpacity="0.1" />
        <path d="M 50 52 L 20 65 L 5 42 Z" fill="black" fillOpacity="0.05" />

        {/* Center highlight for extra puffiness */}
        <circle cx="50" cy="52" r="10" fill="white" fillOpacity="0.2" filter="blur(3px)" />
      </svg>
      {/* Value Overlay */}
      <span className={cn(
        "relative z-10 font-num text-xl font-black transition-colors drop-shadow-md",
        isSelected ? "text-amber-900" : "text-amber-950"
      )}>
        {value}
      </span>
    </div>
  );
};

export default function AppleGame({ onGameOver, onBack, onUpdateMission, prismBuffs, isPractice = false, tutorialStep, onNextTutorialStep }: AppleGameProps) {
  const [grid, setGrid] = useState<number[][]>([]);
  const [score, setScore] = useState(0);
  const isTutorialActive = tutorialStep !== undefined && (tutorialStep === 34 || tutorialStep === 35);
  const [timeLeft, setTimeLeft] = useState(INITIAL_TIME + (prismBuffs?.extraTime || 0));
  const [startSequence, setStartSequence] = useState<'ready' | 'go' | null>(() => isTutorialActive ? null : 'ready');
  const [selection, setSelection] = useState<{ start: { x: number, y: number }, end: { x: number, y: number } } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [errorFlash, setErrorFlash] = useState(false);
  const [isLocked, setIsLocked] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [bonusExp, setBonusExp] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Stats no longer tracked in Star Burst but required by onGameOver interface
  const correctAnswers = 0;
  const mistakes = 0;
  const feverAnswers = 0;
  const feverTriggerCount = 0;


  const checkPossibleMoves = useCallback((currentGrid: number[][]) => {
    // Check all possible rectangles
    for (let r1 = 0; r1 < ROWS; r1++) {
      for (let c1 = 0; c1 < COLS; c1++) {
        if (currentGrid[r1][c1] === 0) continue;
        for (let r2 = r1; r2 < ROWS; r2++) {
          for (let c2 = c1; c2 < COLS; c2++) {
            let sum = 0;
            let hasNonZero = false;
            for (let r = r1; r <= r2; r++) {
              for (let c = c1; c <= c2; c++) {
                if (currentGrid[r][c] !== 0) {
                  sum += currentGrid[r][c];
                  hasNonZero = true;
                }
              }
            }
            if ((sum === 10 || sum === 20 || sum === 30) && hasNonZero) return true;
          }
        }
      }
    }
    return false;
  }, []);

  const initGrid = useCallback(() => {
    let newGrid: number[][] = [];
    if (tutorialStep === 34) {
      newGrid = Array.from({ length: ROWS }, (_, r) => 
        Array.from({ length: COLS }, (_, c) => {
          if (isInsideTutorialBox(r, c)) {
            return 0;
          }
          return Math.floor(Math.random() * 9) + 1;
        })
      );
      
      // Box 1: 3 + 2 + 1 + 4 = 10 (row 2-3, col 2-3)
      newGrid[2][2] = 3;
      newGrid[2][3] = 2;
      newGrid[3][2] = 1;
      newGrid[3][3] = 4;
      
      // Box 2: row 5-6, col 1-3 -> Sum to 20
      newGrid[5][1] = 5;
      newGrid[5][2] = 2;
      newGrid[5][3] = 3;
      newGrid[6][1] = 4;
      newGrid[6][2] = 1;
      newGrid[6][3] = 5;
      
      // Box 3: row 8-9, col 4-6 -> Sum to 30
      newGrid[8][4] = 6;
      newGrid[8][5] = 4;
      newGrid[8][6] = 5;
      newGrid[9][4] = 7;
      newGrid[9][5] = 3;
      newGrid[9][6] = 5;
      
      setGrid(newGrid);
      return;
    }

    let attempts = 0;
    
    do {
      newGrid = [];
      let totalSum = 0;
      for (let y = 0; y < ROWS; y++) {
        const row = [];
        for (let x = 0; x < COLS; x++) {
          const val = Math.floor(Math.random() * 9) + 1;
          row.push(val);
          totalSum += val;
        }
        newGrid.push(row);
      }

      // Force total sum to multiple of 10
      const remainder = totalSum % 10;
      if (remainder !== 0) {
        const adjustment = 10 - remainder;
        let remainingAdjustment = adjustment;
        for (let y = 0; y < ROWS && remainingAdjustment > 0; y++) {
          for (let x = 0; x < COLS && remainingAdjustment > 0; x++) {
            const currentVal = newGrid[y][x];
            const canAdd = 9 - currentVal;
            const shift = Math.min(canAdd, remainingAdjustment);
            newGrid[y][x] += shift;
            remainingAdjustment -= shift;
          }
        }
      }
      attempts++;
    } while (!checkPossibleMoves(newGrid) && attempts < 10);
    
    setGrid(newGrid);
  }, [checkPossibleMoves, tutorialStep]);

  useEffect(() => {
    initGrid();
  }, [initGrid]);

  useEffect(() => {
    if (showHowTo || startSequence !== null) return;
    audioEngine.startMusic('starburst');
    // Music routing handled by App.tsx
  }, [showHowTo, startSequence]);


  useEffect(() => {
    if (showHowTo) return;
    if (startSequence === 'ready') {
      audioEngine.playSound('ready');
      const t = setTimeout(() => setStartSequence('go'), 1000);
      return () => clearTimeout(t);
    } else if (startSequence === 'go') {
      audioEngine.playSound('go');
      const t = setTimeout(() => setStartSequence(null), 1000);
      return () => clearTimeout(t);
    }
  }, [startSequence, showHowTo]);

  const handleGameOver = useCallback(() => {
    onGameOver({
      score,
      correctAnswers: isPractice ? 0 : correctAnswers,
      mistakes: isPractice ? 0 : mistakes,
      feverAnswers: isPractice ? 0 : feverAnswers,
      feverTriggerCount: isPractice ? 0 : feverTriggerCount,

      bonusExp: isPractice ? 0 : bonusExp
    });
  }, [score, correctAnswers, mistakes, feverAnswers, bonusExp, onGameOver, isPractice]);

  useEffect(() => {
    if (timeLeft === 0) {
      handleGameOver();
    }
  }, [timeLeft, handleGameOver]);

  useEffect(() => {
    if (startSequence || timeLeft === 0 || showHowTo || isTutorialActive) return;
    
    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [startSequence, showHowTo, timeLeft === 0, isTutorialActive]);

  const handlePointerDown = (e: React.PointerEvent) => {
    if (startSequence || isLocked) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setSelection({ start: { x, y }, end: { x, y } });
    setIsDragging(true);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging || !selection || isLocked) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    setSelection({ ...selection, end: { x, y } });
  };

  const getSelectedRange = useCallback(() => {
    if (!selection || !containerRef.current) return null;
    const rect = containerRef.current.getBoundingClientRect();
    const cellWidth = rect.width / COLS;
    const cellHeight = rect.height / ROWS;

    const x1 = Math.min(selection.start.x, selection.end.x);
    const x2 = Math.max(selection.start.x, selection.end.x);
    const y1 = Math.min(selection.start.y, selection.end.y);
    const y2 = Math.max(selection.start.y, selection.end.y);

    const padX = cellWidth * 0.35;
    const padY = cellHeight * 0.35;

    const effX1 = x1 + padX;
    const effX2 = x2 - padX;
    const centerX = (x1 + x2) / 2;
    const finalX1 = effX1 > effX2 ? centerX : effX1;
    const finalX2 = effX1 > effX2 ? centerX : effX2;

    const effY1 = y1 + padY;
    const effY2 = y2 - padY;
    const centerY = (y1 + y2) / 2;
    const finalY1 = effY1 > effY2 ? centerY : effY1;
    const finalY2 = effY1 > effY2 ? centerY : effY2;

    return {
      r1: Math.max(0, Math.min(ROWS - 1, Math.floor(finalY1 / cellHeight))),
      r2: Math.max(0, Math.min(ROWS - 1, Math.floor(finalY2 / cellHeight))),
      c1: Math.max(0, Math.min(COLS - 1, Math.floor(finalX1 / cellWidth))),
      c2: Math.max(0, Math.min(COLS - 1, Math.floor(finalX2 / cellWidth)))
    };
  }, [selection]);

  const handlePointerUp = useCallback(() => {
    if (!isDragging || !selection || isLocked) return;
    setIsDragging(false);

    const range = getSelectedRange();
    if (!range) return;

    const { r1, r2, c1, c2 } = range;

    if (tutorialStep === 34) {
      const matchedBox = TUTORIAL_BOXES.some(
        box => box.r1 === r1 && box.r2 === r2 && box.c1 === c1 && box.c2 === c2
      );
      if (!matchedBox) {
        audioEngine.playWrong();
        setErrorFlash(true);
        setTimeout(() => {
          setErrorFlash(false);
        }, 500);
        setSelection(null);
        return;
      }
    }

    let sum = 0;
    const selectedIndices: { r: number, c: number }[] = [];

    for (let r = r1; r <= r2; r++) {
      for (let c = c1; c <= c2; c++) {
        if (grid[r][c] !== 0) {
          sum += grid[r][c];
          selectedIndices.push({ r, c });
        }
      }
    }

    if (sum === 10 || sum === 20 || sum === 30) {
      audioEngine.playCorrect(0, 'starburst');
      const newGrid = [...grid.map(row => [...row])];
      selectedIndices.forEach(({ r, c }) => {
        newGrid[r][c] = 0;
      });
      setGrid(newGrid);
      
      const points = sum === 10 ? 1 : sum === 20 ? 2 : 3;
      
      if (!isPractice && onUpdateMission) {
        onUpdateMission('ten_folder', 1);
        onUpdateMission('star_10_master', 1);
      }
      
      const newScore = score + points;
      setScore(newScore);

      if (tutorialStep === 34 && newScore >= 6) {
        onNextTutorialStep?.();
        return;
      }

    } else if (sum > 0) {
      audioEngine.playWrong();
      setErrorFlash(true);
      setBonusExp(prev => Math.max(0, prev - 10)); // Small penalty
      setTimeout(() => {
        setErrorFlash(false);
      }, 500);
    }

    setSelection(null);
  }, [isDragging, selection, grid, isLocked, isPractice, onUpdateMission, getSelectedRange, score, tutorialStep, onNextTutorialStep]);

  const selectedRange = getSelectedRange();

  const isBoxActive = (box: typeof TUTORIAL_BOXES[0]) => {
    for (let r = box.r1; r <= box.r2; r++) {
      for (let c = box.c1; c <= box.c2; c++) {
        if (grid[r] && grid[r][c] !== 0) return true;
      }
    }
    return false;
  };

  const getTutorialBoxStyle = (box: typeof TUTORIAL_BOXES[0]) => {
    const left = (box.c1 / COLS) * 100;
    const top = (box.r1 / ROWS) * 100;
    const width = ((box.c2 - box.c1 + 1) / COLS) * 100;
    const height = ((box.r2 - box.r1 + 1) / ROWS) * 100;
    return {
      left: `${left}%`,
      top: `${top}%`,
      width: `${width}%`,
      height: `${height}%`,
    };
  };

  useEffect(() => {
    window.addEventListener('pointerup', handlePointerUp);
    return () => window.removeEventListener('pointerup', handlePointerUp);
  }, [handlePointerUp]);

  return (
    <div 
      className={cn(
        "flex flex-col h-full transition-all duration-700 p-4 select-none relative overflow-hidden",
        "bg-gradient-to-b from-blue-950 to-black"
      )}
    >
      {/* Background Night Sky Pattern */}
      <div className="absolute inset-0 opacity-40 pointer-events-none" style={{ backgroundImage: 'radial-gradient(#ffffff 1px, transparent 1px)', backgroundSize: '40px 40px' }} />
      
      <AnimatePresence>
        {showHowTo && (
          <HowToPlay mode="apple" onClose={() => setShowHowTo(false)} />
        )}
      </AnimatePresence>

      <GameModal
        isOpen={showExitConfirm}
        onClose={() => setShowExitConfirm(false)}
        onConfirm={onBack}
        type="confirm"
        title="QUIT GAME?"
        message="Your heart will not be refunded and your current progress will be lost. Are you sure?"
        confirmLabel="QUIT"
        cancelLabel="STAY"
      />
      <AnimatePresence>
        {startSequence && !showHowTo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 z-[100] flex items-center justify-center bg-slate-950/90 backdrop-blur-md"
          >
            <motion.div
              key={startSequence}
              initial={{ scale: 0.5, opacity: 0, rotate: -20 }}
              animate={{ scale: 1, opacity: 1, rotate: 0 }}
              exit={{ scale: 2, opacity: 0 }}
              transition={{ type: "spring", damping: 12 }}
              className="text-center"
            >
              <div className="text-8xl font-num text-cyan-400 drop-shadow-[0_0_20px_rgba(34,211,238,0.5)] mb-4">
                {startSequence === 'ready' ? 'READY?' : 'GO!'}
              </div>
              <div className="text-slate-400 font-black uppercase tracking-[0.3em] text-sm">
                {startSequence === 'ready' ? 'FIND THE TENS!' : 'DRAG TO BOX 10!'}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Utility Field Container */}
      <div className="bg-white shadow-[0_10px_30px_rgba(0,0,0,0.5)] rounded-[32px] p-6 mb-4 border-b-8 border-slate-200 relative z-10 overflow-hidden ring-4 ring-white/10">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none mix-blend-multiply" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '6px 6px' }} />
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <button 
            onClick={() => {
              if (isPractice || isTutorialActive) {
                onBack();
              } else {
                setShowExitConfirm(true);
              }
            }} 
            className="p-2 bg-[#fef3c7] text-[#d97706] rounded-xl border-b-[3px] border-[#f59e0b] shadow-sm active:translate-y-[3px] active:border-b-0 transition-all relative z-10"
          >
            <ArrowLeft className="w-5 h-5" strokeWidth={3} />
          </button>
          <div className="flex flex-col items-center">
            {isPractice && (
              <div className="px-3 py-1 bg-slate-100 rounded-full border border-slate-200 mb-1">
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Practice Mode</span>
              </div>
            )}
            <motion.span 
              key={score}
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              className="text-4xl font-num text-[#f59e0b] drop-shadow-sm relative z-10"
            >
              {score}
            </motion.span>
          </div>
          <div className="w-10" />
        </div>

        {/* Timer Row */}
        <div className="w-full">
          {/* Timer Section */}
          <div className="space-y-2 relative z-10">
            <div className="flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-xl border border-slate-100 shadow-inner">
              <Timer className={cn("w-4 h-4 transition-colors duration-500", timeLeft <= 10 ? "text-rose-500 animate-pulse" : (timeLeft <= 30 ? "text-yellow-500" : "text-emerald-500"))} />
              <span className={cn("text-xl font-num tabular-nums font-black transition-colors duration-500", timeLeft <= 10 ? "text-rose-500" : "text-slate-700")}>
                {Math.floor(timeLeft)}s
              </span>
            </div>
            <div className="h-4 bg-slate-100 rounded-full overflow-hidden border-none shadow-inner p-0.5">
              <motion.div
                className={cn("h-full rounded-full transition-colors duration-500", timeLeft <= 10 ? "bg-rose-500" : (timeLeft <= 30 ? "bg-yellow-400" : "bg-emerald-400"))}
                initial={{ width: '100%' }}
                animate={{ width: `${(timeLeft / INITIAL_TIME) * 100}%` }}
                transition={{ type: 'tween', ease: 'linear', duration: 1 }}
              />
            </div>
          </div>
        </div>
      </div>

      <div 
        ref={containerRef}
        className={cn(
          "flex-1 bg-slate-900 shadow-[0_30px_60px_rgba(0,0,0,0.8),inset_0_0_20px_rgba(15,23,42,0.8)] relative touch-none transition-all duration-500 rounded-[40px] !p-2 border-b-[12px] border-slate-950 ring-4 ring-white/10 overflow-hidden",
          isLocked && "opacity-80 brightness-50"
        )}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
      >
        {/* Sky gradient background overlay */}
        <div className="absolute inset-0 opacity-[0.1] pointer-events-none mix-blend-multiply" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '6px 6px' }} />
        <div className="absolute inset-0 shadow-[inset_0_4px_15px_rgba(51,65,85,0.7)] pointer-events-none rounded-[40px]" />
        
        {/* Tutorial correct answer highlights */}
        {tutorialStep === 34 && TUTORIAL_BOXES.map((box, idx) => {
          if (!isBoxActive(box)) return null;
          const style = getTutorialBoxStyle(box);
          return (
            <motion.div
              key={idx}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="absolute border-4 border-dashed border-cyan-400 rounded-2xl pointer-events-none z-30 shadow-[0_0_20px_rgba(34,211,238,0.4)] flex items-center justify-center bg-cyan-400/10 animate-pulse"
              style={style}
            >
              <span className="absolute -top-6 left-1/2 -translate-x-1/2 bg-cyan-400 text-slate-900 font-num text-[10px] font-black px-2 py-0.5 rounded-full whitespace-nowrap shadow-md uppercase tracking-tight z-40">
                {box.label}
              </span>
            </motion.div>
          );
        })}
        <div 
          className="grid h-full w-full"
          style={{ 
            gridTemplateColumns: `repeat(${COLS}, 1fr)`,
            gridTemplateRows: `repeat(${ROWS}, 1fr)`
          }}
        >
          {grid.map((row, r) => (
            row.map((val, c) => {
              const isSelected = selectedRange && r >= selectedRange.r1 && r <= selectedRange.r2 && c >= selectedRange.c1 && c <= selectedRange.c2;
              const isDimmed = isTutorialActive && !isInsideTutorialBox(r, c);
              return (
                <div 
                  key={`${r}-${c}`}
                  className={cn(
                    "flex items-center justify-center relative",
                    isDimmed && "opacity-20 pointer-events-none grayscale-[50%]"
                  )}
                >
                  {val !== 0 && (
                    <motion.div 
                      layout
                      exit={{ scale: 0, opacity: 0, rotate: 180 }}
                      className="relative flex items-center justify-center w-full h-full p-1.5"
                    >
                        <StarIcon value={val} isSelected={isSelected || false} />
                    </motion.div>
                  )}
                </div>
              );
            })
          ))}
        </div>

        {selection && (
          <div 
            className={cn(
              "absolute border-[4px] z-20 pointer-events-none rounded-xl bg-white/10",
              errorFlash ? "border-rose-500 shadow-[0_0_30px_rgba(244,63,94,0.4)]" : "border-yellow-400 shadow-[0_0_40px_rgba(251,191,36,0.6)]"
            )}
            style={{
              left: Math.min(selection.start.x, selection.end.x),
              top: Math.min(selection.start.y, selection.end.y),
              width: Math.abs(selection.start.x - selection.end.x),
              height: Math.abs(selection.start.y - selection.end.y)
            }}
          >
            {/* Corner Sparks */}
            <div className="absolute -top-1 -left-1 w-2 h-2 bg-white rounded-full shadow-[0_0_10px_white]" />
            <div className="absolute -top-1 -right-1 w-2 h-2 bg-white rounded-full shadow-[0_0_10px_white]" />
            <div className="absolute -bottom-1 -left-1 w-2 h-2 bg-white rounded-full shadow-[0_0_10px_white]" />
            <div className="absolute -bottom-1 -right-1 w-2 h-2 bg-white rounded-full shadow-[0_0_10px_white]" />
          </div>
        )}
      </div>

      <div className="mt-2 text-center">
        <p className="text-[10px] font-black text-white/30 uppercase tracking-[0.2em]">
          DRAG A <span className="text-yellow-400">RECTANGLE</span> THAT SUMS TO 10, 20, OR 30
        </p>
      </div>
    </div>
  );
}
